import http from '../http-common';

// Cache for currency settings
let currencySettings = null;
let currencySettingsPromise = null;

/**
 * Fetch currency settings from the backend
 */
const fetchCurrencySettings = async () => {
  if (currencySettings) {
    return currencySettings;
  }

  if (currencySettingsPromise) {
    return currencySettingsPromise;
  }

  currencySettingsPromise = (async () => {
    try {
      const response = await http.get('/settings');
      let currency = response.data.settings?.currency || 'USD';
      // Normalize "Q" to "GTQ" for proper formatting, but keep original for symbol
      const currencyCode = currency === 'Q' ? 'GTQ' : currency;
      currencySettings = {
        currency: currencyCode, // Use GTQ for Intl formatting
        symbol: getCurrencySymbol(currency), // Use original "Q" for symbol
        code: currency // Keep original code
      };
      console.log('Currency settings loaded:', currencySettings);
      return currencySettings;
    } catch (error) {
      console.error('Error fetching currency settings:', error);
      // Fall back to USD for this call only. Don't cache it: a 401 before
      // login would otherwise pin "$" for the whole session.
      return { currency: 'USD', symbol: '$', code: 'USD' };
    } finally {
      currencySettingsPromise = null;
    }
  })();

  return currencySettingsPromise;
};

/**
 * Get currency symbol by currency code
 */
export const getCurrencySymbolByCode = (currency) => {
  const symbols = {
    'USD': '$',
    'GTQ': 'Q',
    'Q': 'Q', // Handle "Q" as a currency code (Guatemalan Quetzal)
    'EUR': '€',
    'GBP': '£',
    'MXN': '$',
    'CAD': '$',
    'AUD': '$',
    'JPY': '¥',
    'CNY': '¥',
    'BRL': 'R$',
    'ARS': '$',
    'CLP': '$',
    'COP': '$',
    'PEN': 'S/',
    'CRC': '₡',
    'PAB': 'B/.',
    'HNL': 'L',
    'NIO': 'C$',
    'DOP': 'RD$',
    'JMD': 'J$',
    'TTD': 'TT$',
    'BBD': 'Bds$',
    'BZD': 'BZ$',
    'XCD': '$',
  };
  return symbols[currency] || '$';
};

const getCurrencySymbol = getCurrencySymbolByCode;

/**
 * Get currency locale based on currency code
 */
const getCurrencyLocale = (currency) => {
  const locales = {
    'USD': 'en-US',
    'GTQ': 'es-GT',
    'Q': 'es-GT', // Handle "Q" as a currency code (Guatemalan Quetzal)
    'EUR': 'en-US',
    'GBP': 'en-GB',
    'MXN': 'es-MX',
    'CAD': 'en-CA',
    'AUD': 'en-AU',
    'JPY': 'ja-JP',
    'CNY': 'zh-CN',
    'BRL': 'pt-BR',
    'ARS': 'es-AR',
    'CLP': 'es-CL',
    'COP': 'es-CO',
    'PEN': 'es-PE',
  };
  return locales[currency] || 'en-US';
};

/**
 * Format currency value
 * @param {number} amount - The amount to format
 * @param {Object} options - Optional formatting options
 * @returns {Promise<string>} Formatted currency string
 */
export const formatCurrency = async (amount) => {
  const settings = await fetchCurrencySettings();
  const numAmount = parseFloat(amount) || 0;
  
  // Use Intl.NumberFormat for proper formatting
  return new Intl.NumberFormat(getCurrencyLocale(settings.currency), {
    style: 'currency',
    currency: settings.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Format currency value synchronously (uses cached settings)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrencySync = (amount) => {
  const settings = currencySettings || {
    currency: 'USD',
    symbol: '$',
    code: 'USD'
  };
  const numAmount = parseFloat(amount) || 0;
  
  // Format number with proper decimal places
  const formattedNumber = new Intl.NumberFormat(getCurrencyLocale(settings.currency), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
  
  // For currencies that might have formatting issues, manually add the symbol
  // This ensures we only show one currency symbol
  const symbol = settings.symbol || getCurrencySymbol(settings.currency);
  
  // Return formatted number with currency symbol at the start
  // This ensures consistent formatting and avoids duplicate symbols
  return `${symbol}${formattedNumber}`;
};

/**
 * Get currency symbol
 * @returns {Promise<string>} Currency symbol
 */
export const getCurrencySymbolAsync = async () => {
  const settings = await fetchCurrencySettings();
  return settings.symbol;
};

/**
 * Get currency symbol synchronously
 * @returns {string} Currency symbol
 */
export const getCurrencySymbolSync = () => {
  const settings = currencySettings || {
    currency: 'USD',
    symbol: '$',
    code: 'USD'
  };
  return settings.symbol;
};

/**
 * Clear currency settings cache (call this when settings are updated)
 */
export const clearCurrencyCache = () => {
  currencySettings = null;
  currencySettingsPromise = null;
};

/**
 * Initialize currency settings (call this on app load)
 */
export const initializeCurrency = async () => {
  await fetchCurrencySettings();
};

/**
 * Format currency value with a specific currency code
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The currency code to use (e.g., 'USD', 'GTQ', 'EUR')
 * @returns {string} Formatted currency string
 */
export const formatCurrencyWithCode = (amount, currencyCode) => {
  const currency = currencyCode || 'USD';
  const numAmount = parseFloat(amount) || 0;
  
  // Format number with proper decimal places
  const formattedNumber = new Intl.NumberFormat(getCurrencyLocale(currency), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
  
  // Get the symbol for the currency
  const symbol = getCurrencySymbol(currency);
  
  // Return formatted number with currency symbol at the start
  return `${symbol}${formattedNumber}`;
};

